import React, { useEffect, useState } from 'react'
import { AddToTrolley, Select } from '@sainsburys-tech/bolt'
import { useSelector, connect } from 'react-redux'
import { InView } from 'react-intersection-observer'
import { canUseDom } from '@sainsburys-tech/boltui-utils'
import { ProductImage } from '../ProductImage/ProductImage'
import AddToTrolleyButtonWrapper from '../AddToTrolleyButtonWrapper/AddToTrolleyButtonWrapper'
import FableAddToTrolleyButton from '../FableAddToTrolleyButton/FableAddToTrolleyButton'
import updateDigitalData, { pushEventAndTrack } from '../../helpers/taggingSerializer'
import { randomString } from '../../utils/index'
import { createAnalyticsOptions, pushAction } from './helpers/essentialExtras.tracking'
import { sortSizeVariantsBySizeAsc } from '../AttributeSelector/AttributeSelectorHelpers'
import { fetchExtraDataRequest } from '../../actionCreators/essentialExtras/essentialExtrasActionCreators'

import { useFeatureFlagContext } from '@/javascript/providers/FeatureFlagContext/FeatureFlagContext'

import * as Styled from './EssentialExtras.styles'
import Logger from '../../helpers/Logger'

const logger = Logger.getInstance()

const { Button: AddToTrolleyButton } = AddToTrolley

const addToTrolleyClickOrigin = 'PDP:essentialExtrasBundleATT'

const EssentialExtras = props => {
  const {
    mainProduct,
    id,
    name,
    price,
    variants,
    specialOfferPrice,
    specialOfferText,
    fetchExtraDataRequest: fetchExtraData
  } = props
  const { PDP_V2_FLAG } = useFeatureFlagContext()
  const [selectOptions, setSelectOptions] = useState({})
  const [selectedVariant, setSelectedVariant] = useState(id)
  const dataTest = 'essential-extras'
  const isTu = mainProduct?.partNumber.startsWith('tuc')
  const title = isTu ? 'Complete the look...' : 'Complete your purchase with...'

  const buildSelectOptions = (data, availability) => {
    const selectOption = data.map(option => {
      const outOfStock = availability && availability[option?.partNumber]?.quantityAvailable === 0
      const optionTitle = option.attributes?.find(attribute => attribute?.type === 'Size')?.shortValue
      const optionHelperTitle = outOfStock ? ' - Out of stock' : ` - £${(option?.price).toFixed(2)}`
      return {
        title: optionTitle + optionHelperTitle,
        value: option?.partNumber || '',
        disabled: outOfStock
      }
    })
    return selectOption
  }

  const renderHtml = (dataName, html) => {
    return <p data-test={`${dataTest}-${dataName}`} dangerouslySetInnerHTML={{ __html: `${html}` }} />
  }

  const buildState = () => {
    // Update hero bundles and accessories
    if (canUseDom() && mainProduct && id && name && price) {
      pushEventAndTrack({
        eventInfo: {
          eventName: 'pdp_essentialExtrasBundleLoaded',
          eventAction: 'Essential Extras Bundle Loaded'
        }
      })
    }
  }

  const stores = useSelector(state => ({
    productStore: state.productStore,
    bazaarVoiceReviewStore: state.bazaarVoiceReview.data,
    bazaarVoiceQuestionAnswerStore: state.bazaarVoiceQuestionAnswer.data,
    storeLocatorStore: state.storeLocator.data,
    alternativeProductStore: state.alternatives.data[mainProduct.productId],
    specialOffersCreatorStore: state.specialOffersCreator.data,
    essentialExtrasStore: state.essentialExtras.data
  }))

  const handleAccessoryClick = (accessoryId, accessoryName, accessoryPrice) => {
    return pushEventAndTrack({
      eventInfo: {
        eventName: 'pdp_essentialExtrasProductClicked',
        eventAction: 'PDP Essential Extras Product Clicked'
      },
      attributes: {
        productID: accessoryId,
        productName: accessoryName,
        productPrice: accessoryPrice
      }
    })
  }
  const bundle = [
    { extraId: id, extraName: name, extraPrice: price, ...(specialOfferText && { bundleOffer: specialOfferText }) }
  ]

  const onView = inView => {
    if (inView) {
      pushAction(mainProduct, bundle, null, true)
    }
  }

  const getProductListTracking = () => {
    const options = createAnalyticsOptions(mainProduct, bundle)
    const productList = [
      { id: mainProduct.productId, price: mainProduct.price.now, quantity: 1 },
      ...options.bundles[0].bundleItems.additional
    ]
    return { options, productList }
  }

  const { options, productList } = getProductListTracking()

  const addAccessoriesToTrolley = () => {
    AddToTrolley.onAddToTrolleyModalOpen(productList)(addToTrolleyClickOrigin, options.bundles[0])()
  }

  const isNotABot = () => {
    return /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator?.userAgent) === false
  }

  const ProductLinkWrapper = ({ children }) => {
    return (
      <a
        className='none'
        data-test={`${dataTest}-accessory-link`}
        href={
          isNotABot()
            ? `/product/${selectedVariant}?essentialextras=PDP[${
                mainProduct.productId
              }]:${'heroComponent'}:essentialExtrasBundle:${selectedVariant}:${randomString()}`
            : `/product/${selectedVariant}`
        }
        onClick={() => handleAccessoryClick(selectedVariant, name, price)}
      >
        {children}
      </a>
    )
  }

  const calculatePrice = (mainProductPrice, extraProduct) => {
    let saving = 0
    const totalPrice = mainProductPrice.price.now + extraProduct.price.now
    if (mainProductPrice.price.was) {
      saving += mainProductPrice.price.was - mainProductPrice.price.now
    }
    if (extraProduct.price.was) {
      saving += extraProduct.price.was - extraProduct.price.now
    }
    return [totalPrice, saving]
  }

  let totalPrice
  let saving
  if (specialOfferPrice) {
    totalPrice = specialOfferPrice.nowPrice
    saving = specialOfferPrice.saving
  } else {
    const [calculatedPrice, calculatedSaving] = calculatePrice(mainProduct, { price })
    totalPrice = calculatedPrice
    saving = calculatedSaving
  }
  const totalPriceText = totalPrice ? `£${totalPrice.toFixed(2)}` : ''

  useEffect(() => {
    buildState()
    updateDigitalData(stores, 'essentialExtras Tracking')

    if (isTu) {
      const sortedVariants = sortSizeVariantsBySizeAsc(variants?.attributes?.variants)
      const variantsPartNumber = sortedVariants?.map(variation => variation?.partNumber)
      const fetchAvailability = async () => {
        try {
          const response = await fetch(`/product-api/availability-service/partNumbers/${variantsPartNumber.toString()}`)
          const data = await response.json()
          return data
        } catch (error) {
          logger.error('Error fetching sku', error)
          return null
        }
      }
      const data = fetchAvailability()
      data.then(res => setSelectOptions(buildSelectOptions(sortedVariants, res)))
    }
  }, [])

  const addToTrolleyTrackingParams = {
    clickOrigin: addToTrolleyClickOrigin,
    products: productList,
    bundleOptions: options?.bundles?.[0]
  }

  return (
    <InView as='div' onChange={inView => onView(inView, bundle)} threshold={0.5} triggerOnce>
      <div data-test={`${dataTest}-section`}>
        <Styled.Title>{title}</Styled.Title>
        <Styled.Container PDP_V2_FLAG={PDP_V2_FLAG}>
          <Styled.EssentialExtrasContainer>
            <Styled.ItemsWrapper>
              <Styled.ItemDiv data-test={`${dataTest}-main-item-div`}>
                <ProductImage partNumber={mainProduct.productId} alt={mainProduct.productName} />
                <Styled.ItemName data-test={`${dataTest}-item-details`}>
                  {renderHtml('item-name', mainProduct.productName)}
                  <strong data-test={`${dataTest}-item-price`}>£{mainProduct.price.now.toFixed(2)}</strong>
                  {mainProduct.price.flashText && <p>{mainProduct.price.flashText}</p>}
                </Styled.ItemName>
              </Styled.ItemDiv>

              <Styled.PlusIcon
                src='https://documents.4rgos.it/v1/static/icn-plus'
                data-test={`${dataTest}-plus-icon`}
                key='1-plus-icon'
                alt='plus-icon'
                role='presentation'
              />
              <div>
                <ProductLinkWrapper>
                  <Styled.ItemDiv data-test={`${dataTest}-extra-content-wrapper`}>
                    <ProductImage partNumber={id} alt={name} />
                    <Styled.ItemName data-test={`${dataTest}-accessory-details`}>
                      {renderHtml('accessory-name', name)}
                      <strong data-test={`${dataTest}-accessory-price`}>£{price.now.toFixed(2)}</strong>
                      {price.flashText && <p>{price.flashText}</p>}
                      {specialOfferText && (
                        <Styled.SpecialOfferText data-test={`${dataTest}-special-offer-text`}>
                          {specialOfferText}
                        </Styled.SpecialOfferText>
                      )}
                    </Styled.ItemName>
                  </Styled.ItemDiv>
                </ProductLinkWrapper>
                {selectOptions && selectOptions.length > 0 ? (
                  <Select
                    value={selectedVariant}
                    options={selectOptions}
                    onChange={({ target: { value } }) => {
                      setSelectedVariant(value)
                      fetchExtraData({ partNumber: value, mainProductPartNumber: mainProduct?.partNumber })
                    }}
                    marginBottom={0}
                  />
                ) : null}
              </div>
            </Styled.ItemsWrapper>

            <Styled.ContentFooter data-test={`${dataTest}-content-footer`}>
              <Styled.FooterPricing>
                {saving > 0 && (
                  <Styled.OfferPrice data-test={`${dataTest}-offer-price`}>
                    <span>{`Offer saving `}</span>
                    <span>{`£${parseFloat(saving).toFixed(2)}`}</span>
                  </Styled.OfferPrice>
                )}
                {totalPriceText && (
                  <Styled.TotalPrice data-test={`${dataTest}-total-price`}>
                    <strong>{`Total price: `}</strong>
                    <strong>{totalPriceText}</strong>
                  </Styled.TotalPrice>
                )}
              </Styled.FooterPricing>
              <AddToTrolleyButtonWrapper>
                <AddToTrolleyButton
                  partNumber={[mainProduct.productId, selectedVariant]}
                  full
                  openView
                  disableRecommendations
                  onClick={addAccessoriesToTrolley}
                  data-test={`${dataTest}-att-button`}
                >
                  Add all items to trolley
                </AddToTrolleyButton>
                <FableAddToTrolleyButton
                  productId={mainProduct.productId}
                  bundleItems={[selectedVariant]}
                  quantity={1}
                  trackingParams={addToTrolleyTrackingParams}
                >
                  Add all items to trolley
                </FableAddToTrolleyButton>
              </AddToTrolleyButtonWrapper>
            </Styled.ContentFooter>
          </Styled.EssentialExtrasContainer>
        </Styled.Container>
      </div>
    </InView>
  )
}

const mapDispatchToProps = dispatch => {
  const props = {
    fetchExtraDataRequest: payload => dispatch(fetchExtraDataRequest(payload))
  }
  return props
}

export default connect(null, mapDispatchToProps)(EssentialExtras)
export { EssentialExtras }
